import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { DSSetup, Service } from 'webapp-types';
import type { Theme } from 'webapp/enums/enums';
import { sortBy } from 'webapp/utils/common';

import FormSelectWithActionComponent from 'webapp/components/form/select-with-action';
import type { ActionOption } from 'webapp/components/form/select-with-action';

import type { Goal } from 'webapp/models-custom';
import MetricSetup from 'webapp/models-custom/metric-setup';

export interface DisableGoalInfo {
	model: Goal;
	reason: string;
}

interface Args {
	selectedGoalMetricData: DSSetup.MetricData | null | undefined;
	metricSetup?: MetricSetup | null;
	skipGoals?: Goal[];
	disableGoalsInfo?: DisableGoalInfo[];
	hideEditButton?: boolean;
	isRequired?: boolean;
	size?: 'normal' | 'big';
	theme?: Theme | string;
	hideNewGoalAction?: boolean;
	onlyAutomaticEnabled?: boolean;
	isGoalTypeObjectiveDisabled?: boolean;
	origin?: string;
	onChange: (metricData: DSSetup.MetricData | null) => void;
}

export default class GoalSelectorComponent extends Component<Args> {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;

	sizeClass = this.args.size === 'big' ? `size-big` : '';

	@cached
	get selectedGoalKey() {
		// goal key is unique
		return this.args.selectedGoalMetricData?.key;
	}

	@cached
	get validGoals() {
		const metricSetup = this.args.metricSetup;
		if (metricSetup) {
			return this.mmGoalsService.getValidGoalsForMetricSetup(metricSetup);
		} else {
			return this.mmGoalsService.goalForSpaceModels;
		}
	}

	get shownGoals() {
		const skipGoals = this.args.skipGoals;
		if (!skipGoals?.length) {
			return this.validGoals;
		}

		// Filter out the goals that are in skipGoals (already added to top items)
		const filteredGoals = this.validGoals.filter(
			(goal) => !skipGoals.some((g) => g.goalKeyLong === goal.goalKeyLong),
		);

		// If the selected goal is not in the filtered goals, add it
		const selectedGoal = this.selectedGoal;
		if (selectedGoal && !filteredGoals.includes(selectedGoal)) {
			filteredGoals.push(selectedGoal);
		}

		return filteredGoals;
	}

	@cached
	get goalOptions() {
		const disableGoalInfo = this.args.disableGoalsInfo;
		return sortBy(this.shownGoals, 'order', 'numericId').map((goal) => {
			const disabledInfo = disableGoalInfo?.find(
				(info) => info.model.numericId === goal.numericId,
			);
			return {
				value: goal.goalKeyLong,
				label: goal.title,
				disabled: !!disabledInfo,
				label3: disabledInfo ? disabledInfo.reason : undefined,
			};
		});
	}

	@cached
	get selectedGoal() {
		const key = this.selectedGoalKey;
		if (key) {
			return this.validGoals.find((g) => g.goalKeyLong === key) || null;
		}
		return null;
	}

	get showEditButton() {
		return !this.args.hideEditButton && this.selectedGoal;
	}

	get dropdownActions(): ActionOption[] {
		if (this.args.hideNewGoalAction) {
			return [];
		}

		const text = 'New goal';
		return [
			{
				id: null,
				text,
				isSpecial: true,
				onClick: () => {
					this.createNewGoal();
				},
				position: 'bottom',
				html: `<a class="format-add">${text}</a>`,
			},
		];
	}

	@action
	edit() {
		const goal = this.selectedGoal;
		if (goal) {
			this.mmGoalsService.openEditPopup(goal, 'compare-selector');
		}
	}

	@action
	onChange(key: string | null) {
		const metric = this.mmMetricsService.metrics.find((m) => m.key === key);
		this.args.onChange(metric?.metricData || null);
	}

	@action
	createNewGoal(): void {
		const metricSetup = this.args.metricSetup || new MetricSetup();

		this.mmGoalsService.openNewPopupForMetricSetup(
			metricSetup,
			this.args.origin || '',
			(model: Goal) => {
				const goalKeyLong = model.goalKeyLong;
				if (this.validGoals.find((item) => item.goalKeyLong === goalKeyLong)) {
					this.onChange(goalKeyLong);
				} else {
					this.infolayerService.warning(
						'The goal you created is not compatible with the selection',
					);
				}
			},
			this.args.onlyAutomaticEnabled,
			this.args.isGoalTypeObjectiveDisabled,
		);
	}
	<template>
		<div class="goal-selector {{this.sizeClass}}">
			<FormSelectWithActionComponent
				name="goal"
				@placeholder="Select goal"
				@isNumeric={{false}}
				@isMultiple={{false}}
				@actionOptions={{this.dropdownActions}}
				@selected={{this.selectedGoalKey}}
				@options={{this.goalOptions}}
				@onChange={{this.onChange}}
				@isRequired={{@isRequired}}
				@theme={{@theme}}
			/>

			{{#if this.showEditButton}}
				<a
					class="mk-edit-button mk-edit-button--{{this.navigationService.theme.id}}"
					{{on "click" this.edit}}
				>
					edit
				</a>
			{{/if}}
		</div>
	</template>
}
